import { Users, ShieldCheck, Target, Compass, GraduationCap } from "lucide-react";

const P = "The Krimzon Promise";
const TECH = "Technology makes it easier. Our people make it human.";

export const SERVICES = [
  {
    slug: "staffing",
    verb: "BUILD",
    title: "Staffing & Workforce Solutions",
    icon: Users,
    image: "/img/staffing_solutions_v2.jpg",
    tagline: "Build the workforce you need to grow.",
    summary:
      "From blue- and white-collar staffing to workforce deployment and staff augmentation, KRIMZON helps you build and manage the workforce you need with the right people, processes and support. Digital-first onboarding, mobile-enabled employee experience, geo-enabled workforce visibility and real-time reporting bring greater control, transparency and ease to workforce management.",
    headline: "Build the workforce you need to grow.",
    intro: [
      "Whether you need skilled professionals, frontline workers or additional workforce capacity, KRIMZON helps you build and manage the workforce your business needs — with the right people, structured processes and digital visibility.",
    ],
    sections: [
      {
        type: "approach",
        title: "We don't just provide the workforce. We help manage it.",
        paras: ["Across every engagement, KRIMZON supports your workforce — from deployment and attendance to employee support, coordination and reporting."],
      },
      {
        type: "digital",
        heading: "We use digital tools and processes to make workforce management simpler and more transparent.",
        tags: ["Mobile-enabled", "Paperless", "Geo-enabled", "Automated", "Connected"],
        footnote: TECH,
      },
      {
        type: "offerings",
        title: "Our Workforce Solutions",
        items: [
          { name: "White-Collar Staffing", headline: "Skilled people for the roles that keep your business moving.", paras: ["We help organisations build professional teams across functions and locations, with workforce solutions aligned to their business needs."] },
          { name: "Blue-Collar Staffing", headline: "Reliable people for the work that keeps your operations running.", paras: ["We provide frontline and operational workforce solutions where availability, continuity and workforce support matter every day."] },
          { name: "Staff Augmentation", headline: "Extra workforce when you need it.", paras: ["When projects, growth or changing business needs require additional capacity, we provide flexible workforce support without the need to build a permanent team."] },
        ],
      },
      {
        type: "principles",
        eyebrow: "Why Krimzon?",
        title: "Workforce, not just manpower.",
        sub: "We believe staffing is about more than putting people into positions. It is about creating a workforce that is reliable, supported, visible and ready to perform.",
        items: [
          { k: "01", t: "Excellence", d: "We focus on quality, consistency and disciplined execution." },
          { k: "02", t: "Ease", d: "We simplify processes, reduce paperwork and create greater workforce visibility." },
          { k: "03", t: "Experience", d: "We care about how both clients and employees experience the journey by staying involved and taking ownership beyond deployment." },
        ],
      },
      {
        type: "promise",
        statement: "You focus on the work. We focus on enabling your workforce.",
        lines: ["One workforce partner.", "One consistent experience."],
        paras: [],
      },
    ],
    cta: {
      eyebrow: "Building or scaling your workforce?",
      title: "Let's build a workforce that works for your business.",
      text: "With the right people, structured processes and digital visibility to make workforce management simpler, more transparent and easier to manage.",
      button: "Build Your Workforce With Us",
    },
  },
  {
    slug: "payroll-compliance",
    verb: "RUN",
    title: "Payroll & Workforce Compliance",
    icon: ShieldCheck,
    image: "/img/payroll_compliance.jpg",
    tagline: "Run your workforce with confidence.",
    summary:
      "We simplify payroll and workforce administration through accurate payroll processing, statutory compliance, employee lifecycle management and responsive query support. Paperless processes, digital documentation, automated workflows and workforce dashboards create a more efficient, transparent and compliant experience for both clients and employees.",
    headline: "Run your workforce with confidence.",
    intro: [
      "Payroll and compliance should work quietly in the background — accurately, consistently and without creating unnecessary work for your business.",
      "KRIMZON manages payroll processing, workforce administration and statutory compliance through structured processes, digital workflows and responsive human support — giving you greater accuracy, visibility and peace of mind.",
    ],
    sections: [
      {
        type: "approach",
        title: "We simplify payroll and compliance.",
        paras: [
          "From payroll inputs and salary processing to statutory requirements and employee queries, KRIMZON helps manage the administrative side of your workforce with discipline and care.",
          "Our approach combines technology-enabled processes with human oversight, making routine work simpler while ensuring employees have someone to turn to when they need support.",
        ],
      },
      {
        type: "offerings",
        title: "Our Payroll & Compliance Solutions",
        items: [
          { name: "Payroll Management", headline: "Accurate payroll. Delivered on time.", paras: ["We manage the payroll process from inputs and validation through processing, disbursement and reporting — helping ensure employees are paid accurately and on schedule."], tags: ["Payroll Processing", "Salary Disbursement", "Payroll Inputs & Validation", "Payroll Reports"] },
          { name: "Statutory Compliance", headline: "Stay compliant, without the chase.", paras: ["We support the statutory requirements that come with managing a workforce, helping maintain accurate records, timely filings and disciplined compliance processes."], tags: ["PF", "ESIC", "PT", "TDS", "LWF", "Gratuity", "Statutory Records", "Compliance Updates"], note: "The exact statutory scope can be tailored based on the engagement and workforce requirements." },
          { name: "Employee & HR Administration", headline: "Make everyday HR administration easier.", paras: ["We support key employee lifecycle and workforce administration activities, reducing manual effort while giving employees a simpler way to access information and raise routine queries."], tags: ["Employee Documentation", "Lifecycle Administration", "Query & Escalation Support", "Digital Records", "HR MIS"] },
        ],
      },
      {
        type: "digital",
        heading: "Less paperwork. More accuracy. Greater visibility.",
        text: "We use digital tools and workflows to make payroll and workforce administration faster, more transparent and easier to manage.",
        tags: ["Paperless", "Employee Self-Service", "Automated Workflows", "Payroll Visibility", "Digital Records"],
        footnote: TECH,
      },
      {
        type: "statement",
        title: "Payroll is more than numbers. It's trust.",
        lines: ["For an employee, salary is personal.", "For a business, payroll is operational.", "For both, accuracy matters."],
        paras: ["That's why KRIMZON combines process discipline, digital capabilities and human support to make payroll and workforce administration dependable, transparent and responsive."],
        emphasis: ["We don't just process payroll.", "We take ownership of the process and the experience around it."],
      },
      {
        type: "promise",
        statement: "You shouldn't have to chase your payroll partner.",
        paras: ["We believe payroll and compliance should be predictable, transparent and responsive."],
        list: { lead: "You should always know:", items: ["What's happening.", "What's completed.", "What's pending.", "What needs your attention."] },
        after: [
          "And when your employees need help, they should know where to go and who will respond.",
          "That's how we make payroll and workforce administration easier to manage and easier to trust.",
        ],
      },
    ],
    cta: {
      title: "Let's make payroll the easy part.",
      text: "Accurate payroll, disciplined compliance and responsive workforce administration — delivered through disciplined processes that simplify the details and help you manage them with greater confidence.",
      button: "Simplify Your Payroll & Compliance",
    },
  },
  {
    slug: "talent-acquisition",
    verb: "FIND",
    title: "Talent Acquisition & RPO",
    icon: Target,
    image: "/img/talent_acquisition.jpg",
    tagline: "Find people who perform, contribute and belong.",
    summary:
      "We go beyond filling vacancies to understand your business, culture, talent landscape and future needs — combining talent intelligence, specialised sourcing and human judgement to find the right fit. From niche and leadership hiring to volume recruitment, contract-to-hire and RPO, we bring precision, partnership and a candidate experience that reflects your employer brand.",
    headline: "We find people who perform, contribute and belong.",
    lead: "The right hire is more than the right CV.",
    intro: ["KRIMZON helps organisations find and hire the right talent through focused search, thoughtful assessment and a recruitment experience designed around both business needs and people."],
    sections: [
      {
        type: "approach",
        title: "We understand before we hire.",
        paras: [
          "Every organisation, every role and every team is different.",
          "We take the time to understand your business, role requirements, team dynamics and culture before we begin the search.",
          "This helps us look beyond skills and experience to find people who can perform, contribute and belong.",
        ],
      },
      {
        type: "offerings",
        title: "Our Talent Solutions",
        items: [
          { name: "Talent Acquisition", headline: "The right talent for the right role.", paras: ["We help you identify, attract and hire talent across functions, levels and locations with a recruitment approach aligned to your business requirements."], tags: ["Specialised Hiring", "Leadership Hiring", "Volume Hiring", "Niche Skill Hiring"] },
          { name: "RPO", headline: "Your recruitment team, extended.", paras: ["When you need additional recruitment capacity or expertise, our team works as an extension of your organisation.", "From sourcing and screening to candidate engagement, interview coordination and hiring support, we manage the recruitment process around your needs."], tags: ["Sourcing", "Screening", "Candidate Engagement", "Interview Coordination", "Hiring Support", "Recruitment MIS"] },
          { name: "Contract to Hire", headline: "Test the skill before you commit to the seat.", paras: ["When a role calls for a specialised or hard-to-assess skill, Contract to Hire gives you a practical way to test it before making a long-term commitment.", "We manage the workforce engagement while giving both the organisation and the employee an opportunity to assess capability and working fit before the role becomes permanent."], tags: ["Flexible Hiring", "Performance Assessment", "Conversion to Permanent"] },
        ],
      },
      {
        type: "promise",
        statement: "We don't just fill positions. We build the right fit.",
        paras: [
          "For us, successful hiring isn't about closing a vacancy.",
          "It's about finding someone with the skills to do the job and the mindset to contribute.",
        ],
      },
    ],
    cta: {
      title: "Let's find the right people for your business.",
      text: "Whether you're hiring for one critical position, building a team, ramping up a project or looking for an extended recruitment partner, KRIMZON makes the hiring journey simple and helps you find the right fit.",
      button: "Find Your Talent",
    },
  },
  {
    slug: "learning-development",
    verb: "GROW",
    title: "Learning & Development",
    icon: GraduationCap,
    image: "/img/learning_development.jpg",
    tagline: "Grow the capabilities that move your business forward.",
    summary:
      "Our learning interventions are designed around your people, culture and business priorities — never as off-the-shelf programmes. From leadership and manager effectiveness to behavioural, team, sales and culture interventions, we focus on practical learning that translates into meaningful workplace impact.",
    headline: "Grow the people who grow your business.",
    intro: [
      "People don't develop because they attend training. They develop when learning helps them think differently, lead with purpose and perform better.",
      "KRIMZON designs practical, business-aligned learning interventions that address real capability gaps — helping organisations build stronger leaders, better managers and more capable teams.",
    ],
    sections: [
      {
        type: "approach",
        title: "There is no one-size-fits-all learning.",
        paras: [
          "Every organisation has its own culture, business priorities and people challenges.",
          "We first understand what needs to change, why it matters and who needs to change — and then design the learning intervention around it.",
          "From leadership and manager development to behavioural, business and workplace programmes, our approach is practical, relevant and designed around your organisation.",
        ],
      },
      {
        type: "offerings",
        title: "Our Learning Solutions",
        items: [
          { name: "Leadership & Manager Development", headline: "Build leaders who bring out the best in people.", paras: ["We help emerging and experienced leaders strengthen the skills they need to lead with confidence, clarity and accountability."], tags: ["First-Time Manager", "Leadership Development", "Manager Effectiveness", "Performance & Feedback", "Coaching & Mentoring"] },
          { name: "Behavioural & Business Development", headline: "Build the capabilities that make a difference.", paras: ["We design practical interventions that help individuals and teams communicate better, collaborate better and perform better."], tags: ["Team Effectiveness", "Communication", "Customer Service", "Sales & Value Selling", "Cultural Sensitisation", "Diversity & Inclusion"] },
          { name: "Workplace & Compliance Learning", headline: "Create workplaces that are aware, respectful and responsible.", paras: ["Learning also plays an important role in creating safe, inclusive and compliant workplaces.", "We deliver awareness and capability-building programmes around important workplace practices and responsibilities."], tags: ["POSH Awareness & Training", "Workplace Ethics", "Diversity & Inclusion", "Statutory Compliance Awareness", "Employee Awareness Programmes"] },
        ],
      },
      {
        type: "statement",
        eyebrow: "Learning That Creates Change",
        title: "We believe effective learning should translate into something people do differently.",
        paras: ["That's why our interventions can be designed around:"],
        tags: ["Real business situations", "Practical activities", "Role plays & simulations", "Stories, cinema and metaphor-based facilitation", "Reflection & discussion", "Action planning"],
        emphasis: ["The format may change. The objective doesn't — learning that people can apply."],
      },
      {
        type: "promise",
        statement: "Learning should create change, not just attendance.",
        paras: [
          "We don't measure a successful learning intervention by the number of people sitting in a room.",
          "We look at whether people understand, engage, apply and change.",
          "Every intervention is designed to be relevant to the organisation, meaningful to the learner and connected to the outcome the business is trying to achieve.",
        ],
      },
    ],
    cta: {
      title: "Let's build capability that moves your business forward.",
      text: "Whether you are developing new managers, strengthening leadership capability, improving team performance or creating a more aware and responsible workplace, KRIMZON can design a learning intervention around what your organisation needs.",
      button: "Grow With Krimzon",
    },
  },
  {
    slug: "fractional-chro",
    verb: "TRANSFORM",
    title: "HR Transformation & Fractional CHRO Services",
    icon: Compass,
    image: "/img/fractional_chro.jpg",
    tagline: "Transform the way your organisation works.",
    summary:
      "Bring experienced HR leadership to your organisation without the overhead of a full-time CHRO. We partner with founders and leadership teams on people strategy, organisation design, workforce planning, performance, culture, leadership and HR transformation — strategic when you need us, hands-on when it matters.",
    headline: "Transform HR. Build stronger organisations.",
    lead: "Not every organisation needs a full-time CHRO. Not every HR challenge needs a consultant.",
    intro: ["KRIMZON gives you access to the right level of HR expertise, involvement and support based on what your business needs."],
    sections: [
      {
        type: "offerings",
        eyebrow: "How we engage",
        title: "Three ways we can support your HR journey",
        items: [
          { name: "Fractional CHRO", headline: "Your HR leadership, without the full-time CHRO cost.", paras: ["Our Fractional CHRO works as your HR leader — partnering with the management team and taking responsibility for the organisation's people agenda.", "From HR strategy and workforce planning to policies, performance, culture, talent and critical people decisions, we bring the experience and judgement expected from a CHRO or HR Head."], tags: ["HR Strategy", "People & Culture", "Workforce Planning", "Talent Management", "Performance", "Policies & Governance", "Leadership Support", "Critical People Decisions"] },
          { name: "Building & Transforming HR Function", headline: "Build the HR foundation your organisation needs.", paras: ["We help organisations set up, structure or transform their HR function — putting the right policies, processes and people programmes in place.", "Our role is to design and build the framework. Your team drives it."], tags: ["HR Function Design", "Policies & Processes", "HR Frameworks", "People Programmes", "Performance Frameworks", "Employee Lifecycle", "HR Governance"] },
          { name: "Talent & Leadership Advisory", headline: "Experienced perspective when it matters.", paras: ["Sometimes you don't need a CHRO or a full HR transformation.", "You simply need an experienced HR perspective to help management think through an important people decision, initiative or challenge.", "We work with leadership teams on agreed projects, initiatives or defined periods — bringing experience, objectivity and practical perspectives to the table."], tags: ["Talent Strategy", "Leadership Advisory", "Organisation Effectiveness", "People & Culture", "Critical HR Initiatives", "Management Advisory"] },
        ],
      },
      {
        type: "promise",
        statement: "The right HR support for the stage you're in.",
        paras: [
          "You don't need to build a large HR function to get access to experienced HR leadership.",
          "Whether you need someone to lead HR, build HR or advise you on a specific challenge, KRIMZON brings the right level of expertise and involvement — without adding unnecessary complexity.",
        ],
      },
    ],
    cta: {
      title: "Let's build the HR your business needs.",
      text: "Whether you're setting up HR for the first time, transforming an existing function or looking for experienced guidance on a critical people matter, let's empower HR to drive people, culture and business outcomes.",
      button: "Transform Your HR",
    },
  },
];

export const PROMISE_EYEBROW = P;
export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
